import React from 'react';
import { GUIDE_SURFACES, GuideLink } from '@features/knowledge-base/links';
import { Box, Stack, Typography } from '@mui/material';

import { useEligibilityBadgeState } from '../hooks/useEligibilityBadgeState';
import { ELIG_TOKENS, getAlertSeverityColor } from '../theme/readinessTokens';

import { DetailSection } from './EligibilityFinancialsBlock';

export type EligibilityAlertsVariant = 'compact' | 'full';

interface Props {
  patientId?: string;
  encounterId?: string;
  /**
   * `compact` — title-only list with a leading severity dot. For inline use
   *             inside the card / popover summary.
   * `full`    — title + description + suggested-action caption per alert,
   *             wrapped in a `DetailSection`. For the drawer.
   */
  variant: EligibilityAlertsVariant;
}

/**
 * Renders the BE-supplied `alert_details` array. Self-fetches via
 * `useEligibilityBadgeState` so callers only pass identifiers, matching the
 * pattern used by the financials and coverage blocks.
 */
export const EligibilityAlertsBlock: React.FC<Props> = ({ patientId, encounterId, variant }) => {
  const { eligibilityData: data } = useEligibilityBadgeState({ patientId, encounterId });
  const alerts = data?.alert_details ?? [];
  if (alerts.length === 0) return null;

  if (variant === 'compact') {
    return (
      <Stack spacing={0.5}>
        {alerts.map(alert => (
          <Box key={alert.code} sx={{ display: 'flex', alignItems: 'center', gap: 0.875 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: getAlertSeverityColor(alert.severity),
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body2"
              sx={{ color: ELIG_TOKENS.ink, fontSize: 13, lineHeight: 1.3 }}
            >
              {alert.title}
            </Typography>
          </Box>
        ))}
      </Stack>
    );
  }

  // Full variant — drawer treatment.
  return (
    <DetailSection
      title="Alerts"
      titleControl={
        <GuideLink
          surfaceId={GUIDE_SURFACES.ELIGIBILITY_ALERTS}
          shape="section-control"
          topic="eligibility"
        />
      }
    >
      <Stack spacing={1.5}>
        {alerts.map(alert => (
          <Box key={alert.code} sx={{ display: 'flex', gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: getAlertSeverityColor(alert.severity),
                flexShrink: 0,
                mt: '6px',
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ color: ELIG_TOKENS.ink, fontWeight: 600, lineHeight: 1.35 }}
              >
                {alert.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: ELIG_TOKENS.inkSoft, mt: 0.25, lineHeight: 1.4 }}
              >
                {alert.description}
              </Typography>
              {alert.suggested_action && (
                <Typography
                  variant="caption"
                  sx={{
                    color: ELIG_TOKENS.inkFaint,
                    display: 'block',
                    mt: 0.5,
                    fontSize: 11,
                  }}
                >
                  Suggested: {alert.suggested_action}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Stack>
    </DetailSection>
  );
};
